import { connect } from 'react-redux';
import { get } from 'lodash';
import i18next from 'i18next';
import React, { useState, useEffect } from 'react';

import utilsXLS, { EXPORT_DECIMALS_COUNT } from '@commons/utils/makeXLS';

import { exitModal, exportReady, exportFailure } from '@commons/utils/exportModalWithProgress';
import ExportModalContent from '@lib/inpulse/ExportModal';

import { getPropertyNoneValue } from '@commons/constants/categoryTypes';

import { ingredient as ingredientService } from '@services/ingredient';

const BATCH_INGREDIENTS_TO_FETCH_AT_ONCE = 50;

const getColumnsSettings = () => [
  {
    propertyKey: 'id',
    name: i18next.t('GENERAL.ID'),
  },
  {
    propertyKey: 'name',
    name: i18next.t('ADMIN.INGREDIENTS.EXPORT_LABEL_NAME'),
  },
  {
    propertyKey: 'status',
    name: i18next.t('GENERAL.STATUS'),
  },
  {
    propertyKey: 'category',
    name: i18next.t('ADMIN.INGREDIENTS.EXPORT_LABEL_CATEGORY'),
  },
  {
    propertyKey: 'unit',
    name: i18next.t('ADMIN.INGREDIENTS.EXPORT_LABEL_UNIT'),
  },
  {
    type: 'currency',
    propertyKey: 'cost',
    name: i18next.t('ADMIN.RECIPES.EXPORT_COLUMN_COST'),
    decimals: EXPORT_DECIMALS_COUNT,
  },
  {
    propertyKey: 'supplierProductNumber',
    name: i18next.t('ADMIN.INGREDIENTS.EXPORT_LABEL_SUPPLIER_PRODUCTS'),
  },
  {
    propertyKey: 'allergens',
    name: i18next.t('ADMIN.INGREDIENTS.EXPORT_LABEL_ALLERGENS'),
  },
];

/**
 * Format a given ingredient,  make sure to get all information that would be necessary
 * for generating the export file
 *
 * @param {Object} ingredient - The ingredient to format
 *
 * @return {Object[]} The list of formatted data from the given ingredient
 */
export function formatDataForXLSFile(ingredient) {
  const supplierProductNumber = ingredient.supplierProducts.length;
  const allergens = ingredient.allergens
    .map(({ name, translationKey }) => i18next.t(`ADMIN.INGREDIENTS.${translationKey}`) || name)
    .sort()
    .join(', ');
  const status = ingredient.active ? i18next.t('GENERAL.ACTIVE') : i18next.t('GENERAL.INACTIVE');

  const unit =
    ingredient.unit === 'unit'
      ? 'Unité'
      : ingredient.unit === 'g'
      ? 'Kg'
      : ingredient.unit === 'mL'
      ? 'L'
      : ingredient.unit;

  return {
    ...ingredient,
    unit,
    supplierProductNumber,
    allergens,
    status,
  };
}

/**
 * Update the progress value from the elements that are still left to treat
 *
 * @param {String[]} ingredientIds           - The list of ingredient ids that are still to be treated
 * @param {Object[]} detailedIngredientList  - The list of ingredient that has already been treated
 * @param {Function} setProgress          - Method to set the local state of the progress value
 *
 * @return {void}
 */
export function updateProgress(ingredientIds, detailedIngredientList, setProgress) {
  const ingredientLeft = ingredientIds.length;
  const totalIngredients = ingredientLeft + detailedIngredientList.length;
  const updatedProgress = 100 - (ingredientLeft / totalIngredients) * 100;

  setProgress(updatedProgress);
}

/**
 * Handle the progressive loading of ingredients from the list of ingredientIds that are still to fetch
 *
 * @returns {void}
 */
export async function loadProductsByBatchOfIds(
  ingredientIds,
  detailedIngredientList,
  setLoading,
  setTitle,
  setProgress,
  setIngredientIds,
  setDetailedIngredientList,
  currency,
) {
  if (!ingredientIds.length && !detailedIngredientList.length) {
    return;
  }

  // If no more ids, then all data has been fetched
  if (!ingredientIds.length) {
    setProgress(100);

    const columns = getColumnsSettings();

    const sheet = utilsXLS.generateDefaultSheet(
      i18next.t('GENERAL.INGREDIENT_PLURAL'),
      columns,
      detailedIngredientList,
      currency,
    );

    utilsXLS.makeXLS(i18next.t('GENERAL.INGREDIENT_PLURAL'), [sheet]);

    return exportReady(setLoading, setTitle);
  }

  const selectedIngredientIds = ingredientIds.splice(0, BATCH_INGREDIENTS_TO_FETCH_AT_ONCE);

  // Perform request
  try {
    const ingredientsExportData = await ingredientService.getIngredientByIds(selectedIngredientIds);

    const formattedIngredientsToExport = ingredientsExportData.map((ingredient) => ({
      ...ingredient,
      allergens:
        ingredient.supplierProductAllergens?.length > 0
          ? ingredient.supplierProductAllergens
          : ingredient.allergens,
      category: ingredient.category || getPropertyNoneValue(),
    }));

    let ingredientItemsList = [...detailedIngredientList];

    // Format data
    for (const data of formattedIngredientsToExport) {
      ingredientItemsList = ingredientItemsList.concat(formatDataForXLSFile(data, currency));
    }

    setDetailedIngredientList(ingredientItemsList);

    updateProgress(ingredientIds, ingredientItemsList, setProgress);

    setIngredientIds(ingredientIds);
  } catch (err) {
    exportFailure(setLoading, setTitle);
  }
}

export const IngredientExportModal = (props) => {
  const {
    params: { title, ingredients, customParams, selectedStatus, selectedStrategicFilters },
    closeModal,
    currency,
  } = props;

  const [progress, setProgress] = useState(0);
  const [isLoading, setLoading] = useState(true);
  const [titleModal, setTitle] = useState(title || '');
  const [ingredientIdsToProcess, setIngredientIds] = useState([]);
  const [detailedIngredientList, setDetailedIngredientList] = useState([]);

  // States for fetching all ingredients by filters
  const [currentPage, setCurrentPage] = useState(1);
  const [allIngredientIds, setAllIngredientIds] = useState([]);
  const [fetchingPhase, setFetchingPhase] = useState('initial'); // 'initial', 'fetching_ids', 'processing_details', 'completed'

  const fetchNextBatch = async () => {
    try {
      const { data, count } = await ingredientService.getPaginatedIngredientsOfClient(
        {
          orderBy: 'name',
          orderType: 'asc',
          page: currentPage,
          limit: BATCH_INGREDIENTS_TO_FETCH_AT_ONCE,
          search: '', // Don't apply search for export
        },
        {
          active: { eq: get(selectedStatus, 'filterValue', false) },
          isStrategic: {
            inArray: (selectedStrategicFilters || []).map((filter) => filter.itemValue),
          },
          categoryId: customParams?.categoryId,
          unit: customParams?.unit,
          cost: customParams?.cost,
          nbParentMappingsCount: customParams?.parentCounts,
          nbSupplierProductsCount: customParams?.supplierProductMappingCount,
        },
      );

      const ingredientIds = (data || []).map(({ id }) => id);

      // Update allIngredientIds with new batch
      const updatedAllIngredientIds = [...allIngredientIds, ...ingredientIds];
      setAllIngredientIds(updatedAllIngredientIds);

      // Check if we have more data to fetch
      if (
        data &&
        data.length === BATCH_INGREDIENTS_TO_FETCH_AT_ONCE &&
        updatedAllIngredientIds.length < count
      ) {
        // Continue with next batch
        setCurrentPage(currentPage + 1);
      } else {
        // All ingredient IDs fetched, now start the detailed export process
        setFetchingPhase('processing_details');
        setIngredientIds([...updatedAllIngredientIds]);
      }
    } catch {
      exportFailure(setLoading, setTitle);
    }
  };

  // Initialize: either use provided ingredients or fetch all by filters
  useEffect(() => {
    if (ingredients && ingredients.length) {
      // Export selected ingredients directly
      const ingredientIds = ingredients.map((ingredient) => ingredient.id);
      setIngredientIds(ingredientIds);
      setFetchingPhase('processing_details');
    } else if (customParams && selectedStatus) {
      // Start fetching all ingredients by filters
      setFetchingPhase('fetching_ids');
    }
  }, []);

  // Fetch ingredients batch by batch when in fetching_ids phase
  useEffect(() => {
    if (fetchingPhase !== 'fetching_ids') {
      return;
    }

    fetchNextBatch();
  }, [currentPage, fetchingPhase]);

  // Handle the detailed export process
  useEffect(() => {
    if (fetchingPhase === 'processing_details') {
      loadProductsByBatchOfIds(
        [...ingredientIdsToProcess],
        detailedIngredientList,
        setLoading,
        setTitle,
        setProgress,
        setIngredientIds,
        setDetailedIngredientList,
        currency,
      );
    }
  }, [ingredientIdsToProcess, fetchingPhase]);

  return (
    <ExportModalContent
      {...props}
      closeModal={closeModal}
      exitModal={exitModal}
      isLoading={isLoading}
      progress={progress}
      setLoading={setLoading}
      titleModal={titleModal}
    />
  );
};

const mapStateToProps = (state) => ({
  currency: state.baseReducer.currency,
});

export default connect(mapStateToProps)(IngredientExportModal);
